import { useState, useCallback } from 'react'
import { supabase } from '@/lib/supabase/client'

interface InspectionData {
  equipment_id: string
  inspector_id: string
  inspection_date: string
  status: 'pass' | 'fail' | 'conditional'
  notes?: string
  signature?: string
}

export const useInspection = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submitInspection = useCallback(async (data: InspectionData) => {
    setLoading(true)
    setError(null)

    try {
      const { data: inspection, error: insertError } = await supabase
        .from('inspections')
        .insert([data])
        .select()
        .single()

      if (insertError) throw insertError

      // Update equipment's last inspection date
      const { error: updateError } = await supabase
        .from('equipment_v2')
        .update({
          last_inspection_date: data.inspection_date,
          next_inspection_date: calculateNextInspectionDate(data.inspection_date),
        })
        .eq('id', data.equipment_id)

      if (updateError) throw updateError

      return inspection
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to submit inspection'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  const getEquipmentInspections = useCallback(async (equipmentId: string) => {
    setLoading(true)
    setError(null)

    try {
      const { data, error: fetchError } = await supabase
        .from('inspections')
        .select('*')
        .eq('equipment_id', equipmentId)
        .order('inspection_date', { ascending: false })

      if (fetchError) throw fetchError

      return data
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch inspections'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  return {
    loading,
    error,
    submitInspection,
    getEquipmentInspections,
  }
}

// Helper function to calculate next inspection date (6 months from now)
function calculateNextInspectionDate(inspectionDate: string): string {
  const date = new Date(inspectionDate)
  date.setMonth(date.getMonth() + 6)
  return date.toISOString()
}

